import React, { useState, useEffect } from 'react';
import Button from './ui/Button';
import Input from './ui/Input';

interface Group {
  id: number;
  title: string;
  position: number;
  published: number;
  created: string;
  updated: string;
  created_user_id?: number;
  updated_user_id?: number;
}

interface GroupFormProps {
  group?: Group | null;
  onSubmit: (groupData: Omit<Group, 'id'> & { id?: number }) => void;
  onCancel: () => void;
  loading?: boolean;
}

const GroupForm: React.FC<GroupFormProps> = ({ group, onSubmit, onCancel, loading = false }) => {
  const [formData, setFormData] = useState<Omit<Group, 'id'> & { id?: number }>({
    title: '',
    position: 0,
    published: 1,
    created: new Date().toISOString(),
    updated: new Date().toISOString(),
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (group) {
      setFormData({
        id: group.id,
        title: group.title || '',
        position: group.position || 0,
        published: group.published !== undefined ? group.published : 1,
        created: group.created || new Date().toISOString(),
        updated: group.updated || new Date().toISOString(),
      });
    }
  }, [group]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof typeof formData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {group ? 'Edit Group' : 'Add New Group'}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title *
              </label>
              <Input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter group title"
                error={errors.title}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Position
              </label>
              <Input
                type="number"
                value={formData.position}
                onChange={(e) => handleChange('position', parseInt(e.target.value))}
                placeholder="Enter position"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={formData.published}
                onChange={(e) => handleChange('published', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                onClick={onCancel}
                className="bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (group ? 'Update Group' : 'Create Group')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GroupForm; 